/**
 * Remote-control surface (Phase E): Reset, ChangeAvailability,
 * UnlockConnector, TriggerMessage, ClearCache.
 *
 * Each action wraps the gate-block flow:
 *   1. POST without `force`.
 *   2. If 409 with `gate.allowed === false`, prompt the operator.
 *   3. Re-issue with `force=true` only after explicit confirmation.
 *
 * Reset is the single hammer with no blocking statuses on the backend; we
 * still confirm because Hard reset is destructive.
 */

import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';

import OcppActionButton from '../components/OcppActionButton';
import OcppConfirmDialog from '../components/OcppConfirmDialog';
import OcppErrorBanner from '../components/OcppErrorBanner';
import OcppKvList from '../components/OcppKvList';
import OcppSectionCard from '../components/OcppSectionCard';
import { OcppTheme } from '../components/OcppTheme';
import {
  changeAvailability,
  clearCache,
  resetCharger,
  triggerMessage,
  unlockConnector,
} from '../services';
import {
  isOcppSuccess,
  mapOcppApiError,
  type OcppApiErrorInfo,
} from '../utils/apiErrors';
import type {
  ChangeAvailabilityBody,
  ResetBody,
  ResetType,
  TriggerMessageBody,
  TriggerMessageType,
  UnlockConnectorBody,
} from '../models';
import type { OcppTabProps } from './types';

type PendingForceAction = {
  label: string;
  message: string;
  retry: () => Promise<void>;
} | null;

const RESET_TYPES: ResetType[] = ['Soft', 'Hard', 'Immediate', 'OnIdle'];
const TRIGGER_OPTIONS: TriggerMessageType[] = [
  'StatusNotification',
  'Heartbeat',
  'BootNotification',
  'MeterValues',
  'DiagnosticsStatusNotification',
  'FirmwareStatusNotification',
];

const RemoteControlTab: React.FC<OcppTabProps> = ({ chargerId }) => {
  const [error, setError] = useState<OcppApiErrorInfo | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [pendingForce, setPendingForce] = useState<PendingForceAction>(null);

  const [resetType, setResetType] = useState<ResetType>('Soft');
  const [unlockConnectorId, setUnlockConnectorId] = useState<string>('1');
  const [trigger, setTrigger] = useState<TriggerMessageType>('StatusNotification');
  const [availabilityOperative, setAvailabilityOperative] = useState<boolean>(true);

  const handleAction = useCallback(
    async (
      label: string,
      forceMessage: string,
      runner: (force: boolean) => Promise<{ status: number | undefined; payload: unknown }>
    ) => {
      setBusyAction(label);
      setError(null);
      try {
        const res = await runner(false);
        if (isOcppSuccess(res.status)) {
          setLastResult(`${label}: dispatched`);
          return;
        }
        const info = mapOcppApiError(res.status, res.payload);
        if (info.blockedByGate) {
          setPendingForce({
            label,
            message:
              info.message + (forceMessage ? ` ${forceMessage}` : ''),
            retry: async () => {
              setBusyAction(label);
              const second = await runner(true);
              if (isOcppSuccess(second.status)) {
                setLastResult(`${label}: dispatched (forced)`);
                setError(null);
              } else {
                setError(mapOcppApiError(second.status, second.payload));
              }
              setBusyAction(null);
            },
          });
          return;
        }
        setError(info);
      } finally {
        setBusyAction((current) => (current === label ? null : current));
      }
    },
    []
  );

  const onReset = () => {
    void handleAction(
      `Reset (${resetType})`,
      'A hard reset will interrupt any active session.',
      (force) => resetCharger(chargerId, { type: resetType, force } as ResetBody)
    );
  };

  const onUnlock = () => {
    const cid = parseInt(unlockConnectorId, 10);
    if (Number.isNaN(cid)) {
      setError({
        title: 'Invalid request',
        message: 'Connector ID must be a number.',
        blockedByGate: false,
        featureGated: false,
        status: 400,
      });
      return;
    }
    void handleAction(
      `Unlock connector ${cid}`,
      'This will release the cable while a charge may be in progress — the EV may stop charging immediately.',
      (force) => unlockConnector(chargerId, { connector_id: cid, force } as UnlockConnectorBody)
    );
  };

  const onTrigger = () => {
    void handleAction(
      `Trigger ${trigger}`,
      '',
      (force) => triggerMessage(chargerId, { requested_message: trigger, force } as TriggerMessageBody)
    );
  };

  const onAvailability = () => {
    void handleAction(
      availabilityOperative ? 'Make Operative' : 'Make Inoperative',
      'Toggling availability mid-charge can interrupt the session.',
      (force) =>
        changeAvailability(chargerId, {
          operative: availabilityOperative,
          force,
        } as ChangeAvailabilityBody)
    );
  };

  const onClearCache = () => {
    void handleAction('Clear cache', '', () => clearCache(chargerId));
  };

  return (
    <View>
      {error ? <OcppErrorBanner error={error} onRetry={() => setError(null)} /> : null}
      {lastResult ? (
        <View style={styles.resultBanner}>
          <Text style={styles.resultText}>{lastResult}</Text>
        </View>
      ) : null}

      <OcppSectionCard
        title="Reset"
        subtitle="Soft reboots are the safe default. Hard reset is the recovery hammer."
      >
        <View style={styles.chipRow}>
          {RESET_TYPES.map((t) => (
            <Chip
              key={t}
              label={t}
              active={resetType === t}
              onPress={() => setResetType(t)}
            />
          ))}
        </View>
        <OcppActionButton
          label={`Send ${resetType} reset`}
          icon="restart-alt"
          onPress={onReset}
          loading={busyAction?.startsWith('Reset')}
          style={styles.actionRowSpacing}
        />
      </OcppSectionCard>

      <OcppSectionCard
        title="Unlock connector"
        subtitle="Releases the cable. Blocked by Charging / SuspendedEV / SuspendedEVSE unless forced."
      >
        <Text style={styles.fieldLabel}>Connector ID</Text>
        <TextInput
          value={unlockConnectorId}
          onChangeText={setUnlockConnectorId}
          keyboardType="numeric"
          style={styles.input}
          placeholder="1"
          placeholderTextColor={OcppTheme.textDim}
          accessibilityLabel="Connector ID to unlock"
        />
        <OcppActionButton
          label="Unlock connector"
          icon="lock-open"
          onPress={onUnlock}
          loading={busyAction?.startsWith('Unlock')}
          style={styles.actionRowSpacing}
        />
      </OcppSectionCard>

      <OcppSectionCard
        title="Trigger message"
        subtitle="Ask the charger to send the listed OCPP message right now."
      >
        <View style={styles.chipRow}>
          {TRIGGER_OPTIONS.map((t) => (
            <Chip
              key={t}
              label={t}
              active={trigger === t}
              onPress={() => setTrigger(t)}
            />
          ))}
        </View>
        <OcppActionButton
          label={`Trigger ${trigger}`}
          icon="bolt"
          onPress={onTrigger}
          loading={busyAction?.startsWith('Trigger')}
          style={styles.actionRowSpacing}
        />
      </OcppSectionCard>

      <OcppSectionCard
        title="Change availability"
        subtitle="Take the entire charger Operative or Inoperative."
      >
        <View style={styles.chipRow}>
          <Chip
            label="Operative"
            active={availabilityOperative}
            onPress={() => setAvailabilityOperative(true)}
          />
          <Chip
            label="Inoperative"
            active={!availabilityOperative}
            onPress={() => setAvailabilityOperative(false)}
          />
        </View>
        <OcppActionButton
          label={availabilityOperative ? 'Make operative' : 'Make inoperative'}
          icon="power-settings-new"
          onPress={onAvailability}
          loading={busyAction?.startsWith('Make ')}
          style={styles.actionRowSpacing}
        />
      </OcppSectionCard>

      <OcppSectionCard
        title="Clear cache"
        subtitle="Drops the charger's authorization cache so the next swipe re-checks the CSMS."
      >
        <OcppKvList
          items={[
            { key: 'Affects', value: 'Authorization cache only' },
            { key: 'Reversible', value: 'Yes — cache rebuilds on next swipe' },
          ]}
        />
        <OcppActionButton
          label="Clear authorization cache"
          icon="cleaning-services"
          variant="secondary"
          onPress={onClearCache}
          loading={busyAction === 'Clear cache'}
          style={styles.actionRowSpacing}
        />
      </OcppSectionCard>

      <OcppConfirmDialog
        visible={pendingForce !== null}
        title={pendingForce ? `Force ${pendingForce.label}?` : 'Force action?'}
        message={pendingForce?.message ?? ''}
        confirmLabel="Force"
        confirmVariant="danger"
        loading={!!busyAction}
        onCancel={() => setPendingForce(null)}
        onConfirm={async () => {
          const action = pendingForce;
          setPendingForce(null);
          if (action) await action.retry();
        }}
      />
    </View>
  );
};

export default RemoteControlTab;

const Chip: React.FC<{ label: string; active: boolean; onPress: () => void }> = ({
  label,
  active,
  onPress,
}) => (
  <OcppActionButton
    label={label}
    onPress={onPress}
    variant={active ? 'primary' : 'ghost'}
    style={styles.chip}
  />
);

const styles = StyleSheet.create({
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    minHeight: 32,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  actionRowSpacing: {
    marginTop: 12,
    alignSelf: 'flex-start',
  },
  fieldLabel: {
    color: OcppTheme.textMuted,
    fontSize: 12,
    marginBottom: 4,
  },
  input: {
    backgroundColor: OcppTheme.surfaceAlt,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: OcppTheme.border,
    paddingHorizontal: 12,
    paddingVertical: 8,
    color: OcppTheme.textPrimary,
    fontSize: 14,
  },
  resultBanner: {
    backgroundColor: `${OcppTheme.success}1A`,
    borderColor: OcppTheme.success,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
  },
  resultText: {
    color: OcppTheme.success,
    fontSize: 13,
    fontWeight: '600',
  },
});
